import { z } from 'zod';
import { LIMITS } from './constants.js';

/**
 * WebSocket wire protocol for KidzIM chat.
 *
 * Two directions:
 *  - ClientMessage: browser/tablet -> server (always re-validated server-side).
 *  - ServerMessage: server -> all devices in a room (and the supervising parent).
 *
 * Every message is a discriminated union on `t` (type). Keep payloads small.
 */

// ── Client → Server ───────────────────────────────────────────────

export const clientHelloSchema = z.object({
  t: z.literal('hello'),
  /** Last message id the client already has, for catch-up on reconnect. */
  cursor: z.string().nullable().optional(),
});

export const clientSendSchema = z.object({
  t: z.literal('send'),
  /** Idempotency key so retries don't duplicate a message. */
  nonce: z.string().min(8).max(64),
  body: z.string().min(1).max(LIMITS.MAX_MESSAGE_LEN),
});

export const clientTypingSchema = z.object({
  t: z.literal('typing'),
  on: z.boolean(),
});

export const clientMessageSchema = z.discriminatedUnion('t', [
  clientHelloSchema,
  clientSendSchema,
  clientTypingSchema,
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

// ── Server → Client ───────────────────────────────────────────────

export interface WireParticipant {
  id: string;
  screenName: string;
  /** Aligned with `WireMessage.authorKind` — a kid or a supervising grown-up
   *  (parent/admin alike; the server never distinguishes them on the wire). */
  role: 'kid' | 'grownup';
  online: boolean;
}

export interface WireMessage {
  id: string;
  authorId: string;
  authorName: string;
  /** Who wrote it: a kid, a supervising grown-up, or the system. Drives the
   *  "grown-up" badge in the UI so kids can always tell an adult is speaking. */
  authorKind: 'kid' | 'grownup' | 'system';
  /** Sanitized, possibly-redacted body (links/images enforced server-side). */
  body: string;
  /** Moderation outcome applied to this message, if any. */
  moderation: 'clean' | 'link_blocked' | 'image_blocked' | 'flagged';
  createdAt: string;
}

export type ServerMessage =
  | {
      t: 'welcome';
      roomId: string;
      status: 'open' | 'waiting' | 'closed';
      participants: WireParticipant[];
      kind: 'standard' | 'dm';
      /** Display title for the chat chrome: the room's name, or — for a
       *  buddy chat — the other kid's screen name (both kids' names, for a
       *  supervising grown-up observing). */
      title: string;
    }
  | { t: 'waiting'; opensAt: string | null; reason: 'scheduled' | 'closed' | 'budget_exhausted' | 'not_chat_hours' }
  | { t: 'message'; message: WireMessage }
  | { t: 'history'; messages: WireMessage[] }
  | { t: 'presence'; participant: WireParticipant }
  | { t: 'typing'; participantId: string; on: boolean }
  | { t: 'notice'; level: 'info' | 'warn'; text: string }
  | { t: 'closed'; reason: 'schedule_ended' | 'closed_by_parent' | 'budget_exhausted' | 'not_chat_hours' }
  /** Buddy-chat (DM) daily time budget remaining, in seconds; null = unlimited.
   *  Sent on connect and on each metering tick so the kid client can show a
   *  gentle low-time pill. Never sent for standard (non-DM) rooms. */
  | { t: 'im_usage'; remainingSeconds: number | null }
  | { t: 'error'; code: string; text: string };

/** Close codes we use intentionally (4000–4999 app range). */
export const WS_CLOSE = {
  UNAUTHORIZED: 4001,
  FORBIDDEN: 4003,
  ROOM_CLOSED: 4004,
  RATE_LIMITED: 4008,
} as const;
